import { FlowExportSAATemplateFields } from "../types/flowTypes";
import { Destination } from "../types/types";

export const FLOW_EXPORT_SAA_TEMPLATE_FIELDS = "FLOW_EXPORT_SAA_TEMPLATE_FIELDS";

import makeReducerFor from "./_genericDbReducer";
import { createSelector } from "reselect";
import { getFlowItemsForSelectedFlow } from "./flowItems";
import { getFlowExportsForSelectedFlow, getFlowExportsArray } from "./flowExports";
import type { FlowItem, FlowErrorsByItemId, FlowExport } from "../types/stores/flowTypes";

const myGenericReducer = makeReducerFor("FLOW_EXPORT_SAA_TEMPLATE_FIELDS", "FlowExportSAATemplateFieldId");
import subItemReducer from "./_genericFlowSubItemReducer";
import { IAppState } from "../types/stores/index";

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const myReducer = (state = {}, action) => subItemReducer(myGenericReducer(state, action), action);
export default myReducer;

///////// SELECTORS /////////////

export const getFlowExportSAATemplateFieldsArray = createSelector(
    (state: IAppState) => state.flowExportSAATemplateFields.byId,
    (flowExportSAATemplateFieldsById: {
        [number: number]: FlowExportSAATemplateFields;
    }): Array<FlowExportSAATemplateFields> => {
        const r: Array<FlowExportSAATemplateFields> = Object.values(flowExportSAATemplateFieldsById);
        return r;
    }
);

export type FlowExportSAATemplateFieldsByItemId = {
    [number: number]: Array<FlowExportSAATemplateFields>;
};

export const getFlowExportSAATemplateFieldsByFlowItemId = createSelector(
    (state: IAppState) => getFlowExportSAATemplateFieldsArray(state),
    (flowExportSAATemplateFields: Array<FlowExportSAATemplateFields>): FlowExportSAATemplateFieldsByItemId =>
        flowExportSAATemplateFields.reduce((acc, row) => {
            if (acc[row.FlowItemId] == null) {
                acc[row.FlowItemId] = [];
            }
            acc[row.FlowItemId].push(row);
            return acc;
        }, {})
);

export const getFlowExportSAATemplateFieldsForSelectedFlow = createSelector(
    (state: IAppState) => getFlowItemsForSelectedFlow(state),
    (state: IAppState) => getFlowExportSAATemplateFieldsByFlowItemId(state),
    (
        flowItems: Array<FlowItem>,
        flowExportSAATemplateFieldsByItemId: FlowExportSAATemplateFieldsByItemId
    ): Array<FlowExportSAATemplateFields> => {
        let result: Array<FlowExportSAATemplateFields> = [];
        const itemIds = flowItems.map(fi => fi.FlowItemId);
        for (const itemId of itemIds) {
            const templateFields = flowExportSAATemplateFieldsByItemId[itemId];
            if (templateFields != null) {
                result = result.concat(templateFields);
            }
        }
        return result;
    }
);

const saaTemplateFieldsToErrorsById = (
    flowExports: Array<FlowExport>,
    flowExportSAATemplateFields: Array<FlowExportSAATemplateFields>,
    destinations: Array<any>,
    saaTapadDeliveryTypes: Array<any>
): FlowErrorsByItemId => {
    const errorsById = {};
    for (const flowExport of flowExports) {
        errorsById[flowExport.FlowItemId] = validateExportSAATemplateFields(
            flowExport,
            flowExportSAATemplateFields,
            destinations,
            saaTapadDeliveryTypes
        );
    }
    return errorsById;
};

export const getExportSAATemplateFieldErrorsForSelectedFlow = createSelector(
    (state: IAppState) => getFlowExportsForSelectedFlow(state),
    (state: IAppState) => getFlowExportSAATemplateFieldsForSelectedFlow(state),
    (state: IAppState) => state.vars.destinations,
    (state: IAppState) => state.vars.saaTapadDeliveryTypes || [],
    saaTemplateFieldsToErrorsById
);

export const getExportSAATemplateFieldErrorsForAllFlows = createSelector(
    (state: IAppState) => getFlowExportsArray(state),
    (state: IAppState) => getFlowExportSAATemplateFieldsArray(state),
    (state: IAppState) => state.vars.destinations,
    (state: IAppState) => state.vars.saaTapadDeliveryTypes || [],
    saaTemplateFieldsToErrorsById
);

/**
 * Applies numeric validation based on the specified rule type, numeric value, and threshold.
 *
 * @param {string} ruleType - The type of validation rule to apply.
 *                            Supported values include:
 *                            - "MIN_ALLOWED": Checks if the numeric value is less than the threshold.
 *                            - "MAX_ALLOWED": Checks if the numeric value is greater than the threshold.
 *                            - "GREATER_THAN": Checks if the numeric value is less than or equal to the threshold.
 *                            - "LESS_THAN": Checks if the numeric value is greater than or equal to the threshold.
 *                            - "EQUAL": Checks if the numeric value is not equal to the threshold.
 *                            - "LESS_THAN_OR_EQUAL_TO": Checks if the numeric value is greater than the threshold.
 *                            - "GREATER_THAN_OR_EQUAL_TO": Checks if the numeric value is less than the threshold.
 *                            - "MOD": Checks if the numeric value is not divisible by the threshold.
 *                            - "IS_INT": Checks if the numeric value is not an integer.
 * @param {number} numericValue - The numeric value to validate.
 * @param {number} threshold - The threshold value used for validation.
 * @returns {boolean} - Returns `true` if the validation condition is met, otherwise `false`.
 */
export const applyNumericValidation = (ruleType: string, numericValue: number, threshold: number): boolean => {
    const ruleConditions = {
        MIN_ALLOWED: numericValue < threshold,
        MAX_ALLOWED: numericValue > threshold,
        GREATER_THAN: numericValue <= threshold,
        LESS_THAN: numericValue >= threshold,
        EQUAL: numericValue !== threshold,
        LESS_THAN_OR_EQUAL_TO: numericValue > threshold,
        GREATER_THAN_OR_EQUAL_TO: numericValue < threshold,
        MOD: numericValue % threshold !== 0,
        IS_INT: !Number.isInteger(numericValue),
    };

    return ruleConditions[ruleType] || false;
};

/**
 * Validates SAA Template fields based on destination, segment field visibility and validation rules.
 *
 * @param {FlowExport} flowExport - The current export flow object.
 * @param {Array<FlowExportSAATemplateFields>} flowExportSAATemplateFields - The template fields associated with the export flow.
 * @param {Array<Destination>} destinations - List of available destinations.
 * @param {Array<any>} saaTapadDeliveryTypes - SAA segement fields and Validation rules for that delivery type.
 * @returns {Array<string>} - List of validation error messages.
 */
export const validateExportSAATemplateFields = (
    flowExport: FlowExport,
    flowExportSAATemplateFields: Array<FlowExportSAATemplateFields>,
    destinations: Array<Destination>,
    saaTapadDeliveryTypes: Array<any>
): Array<string> => {
    const errors: Array<string> = [];
    const errorFields: Array<string> = [];

    if (!destinations || destinations.length === 0) {
        return errors;
    }

    const destination = destinations.find(x => x.PartnerAccessId === flowExport.DestinationId);
    if (!destination) {
        return errors;
    }

    const templateFieldsList = flowExportSAATemplateFields.filter(x => x.FlowItemId === flowExport.FlowItemId);

    templateFieldsList.forEach(templateFields => {
        if (!templateFields.TemplateId) {
            errors.push("SAA Template ID is required");
            return;
        }

        // Fetch segment fields and validation rules specific to the template ID
        const saaSegmentFields =
            saaTapadDeliveryTypes.find(item => item.SAATemplate.FileBasedSAATemplateId === templateFields.TemplateId)
                ?.SAASegmentFields || [];

        try {
            const fieldValues = JSON.parse(templateFields.ValueJSON);
            const obj = Object.entries(fieldValues);

            saaSegmentFields.forEach(({ SAASegmentField, SAAValidationRules }) => {
                // Find field match based on its visibility
                const keyMatch = obj.find(([key]) => key === SAASegmentField.FieldName && SAASegmentField.IsVisible);

                if (!keyMatch) return;

                const [fieldName, value] = keyMatch;

                // Check if field is required and value is empty or invalid
                if (SAASegmentField.Required && ["", "NaN", null].includes(String(value))) {
                    errorFields.push(fieldName);
                }

                // Validate field value against specified rules
                if (value && SAAValidationRules.length) {
                    SAAValidationRules.forEach(({ RuleType, ValidatorRegex, Severity, Value, Message }) => {
                        if (Severity !== "ERROR") return;

                        if (["REGEX_VALIDATOR", "REGEX"].includes(RuleType)) {
                            if (!new RegExp(ValidatorRegex).test(String(value))) {
                                errors.push(`${SAASegmentField.DisplayName} : ${Message}`);
                            }
                        } else {
                            const threshold = parseFloat(Value);
                            const numericValue = parseFloat(String(value));
                            if (!isNaN(threshold) && !isNaN(numericValue)) {
                                if (applyNumericValidation(RuleType, numericValue, threshold)) {
                                    errors.push(`${SAASegmentField.DisplayName} : ${Message}`);
                                }
                            }
                        }
                    });
                }
            });
        } catch (e) {
            errors.push("Invalid JSON format in SAA Template Field values");
        }
    });

    if (errorFields.length > 0) {
        const errorFieldString = errorFields.map(field => `'${field}'`).join(", ");
        errors.push(`The following SAA Template fields are required: ${errorFieldString}.`);
    }

    return errors;
};
